import { X, Sparkles, Shield, Lock, Users, Inbox, Send, Globe, FolderKanban, BarChart3, Key, Tag, FileKey, Bug, Fingerprint, Eye, Server } from 'lucide-react';

const releases = [
  {
    version: '1.3.0',
    date: '09-Jul-2026',
    label: 'Latest',
    highlights: 'Inbox & Sent, Domain Icons, Plan Usage Dashboard, Categories & Projects',
    sections: [
      {
        title: 'New Features',
        items: [
          { icon: Inbox, text: 'Inbox — View all credentials and certificates shared with you by team members' },
          { icon: Send, text: 'Sent — Track everything you\'ve shared with your team, including recipient details' },
          { icon: Globe, text: 'Smart Domain Icons — Credentials auto-display service logos (Gmail, AWS, Slack, Netflix, etc.) fetched from domain favicons' },
          { icon: BarChart3, text: 'Plan Usage Dashboard — Visual progress bars showing credential, certificate, and user quota usage vs plan limits' },
          { icon: FolderKanban, text: 'Projects — Organize credentials and certificates into project groups with filtering support' },
          { icon: Tag, text: 'Categories — Custom org-level categories (Database, API, Server, etc.) with admin CRUD management' },
          { icon: Key, text: 'Subscription Plan Limits — Starter / Professional / Enterprise plans with configurable limits for credentials, certificates, users, and feature gates' },
          { icon: Users, text: 'Unshare — Revoke shared access on any credential or certificate at any time' }
        ]
      },
      {
        title: 'Improvements',
        items: [
          { icon: Shield, text: 'Role-Based Access tightened — Viewers are read-only, Members can create/share, Admins have full control' },
          { icon: BarChart3, text: 'SaaS Admin can now customize plan limits per organization (max credentials, certificates, users, feature toggles)' },
          { icon: Lock, text: 'Add Credential form no longer pre-fills username/password from browser autofill' },
          { icon: Eye, text: 'Feature access badges on Dashboard show which plan features are enabled (File Attachments, Export, Audit Logs)' },
          { icon: FolderKanban, text: 'Project dropdown filter added to both Credentials and Certificates list pages' },
          { icon: Globe, text: 'Landing page updated with all latest features, What\'s New section, and accurate pricing tiers' }
        ]
      }
    ]
  },
  {
    version: '1.2.0',
    date: '28-Jun-2026',
    label: '',
    highlights: 'OWASP Security, Certificate Management, SaaS Admin Portal',
    sections: [
      {
        title: 'New Features',
        items: [
          { icon: Shield, text: 'Standalone Certificate Management — Full CRUD with provider, format, expiry tracking, and download' },
          { icon: Key, text: 'SaaS Owner Admin Portal (/admin) — Manage all organizations, subscriptions, and user counts' },
          { icon: Send, text: 'Resend Email Notifications — Automated email alerts to SaaS owner on new organization signup' },
          { icon: Users, text: 'Demo Account System — Pre-seeded "KeeDemo Corporation" with admin, member, and viewer accounts' }
        ]
      },
      {
        title: 'Security & Compliance',
        items: [
          { icon: Bug, text: 'OWASP Top 10 Compliance — Rate limiting (slowapi), brute-force protection, strict password policies' },
          { icon: Fingerprint, text: 'Two-Factor Authentication (2FA) — TOTP-based with authenticator app support' },
          { icon: Lock, text: 'Account lockout after 5 failed login attempts with 15-minute cooldown' },
          { icon: Server, text: 'Security headers — HSTS, X-Frame-Options, CSP, XSS-Protection, no-cache' },
          { icon: FileKey, text: 'Secure file uploads — Type validation, 10MB limit, restricted extensions' }
        ]
      },
      {
        title: 'UI & Branding',
        items: [
          { icon: Sparkles, text: 'Rebranded as "Keeboxs" with new landing page, logo, and color scheme' },
          { icon: Eye, text: 'Footer with "Powered by Sysqua Solutions", trust badges, version info, and support emails' },
          { icon: Shield, text: 'Security trust section on landing page with OWASP, SOC 2, GDPR, ISO 27001 badges' }
        ]
      }
    ]
  }
];

export default function ReleaseNotes({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4" data-testid="release-notes-modal">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-gray-200 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="font-manrope font-bold text-2xl text-text-primary tracking-tight">Release Notes</h2>
            <p className="text-text-muted text-sm mt-1">Keeboxs changelog and version history</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="btn-close-release-notes">
            <X size={20} className="text-text-muted" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6 space-y-10">
          {releases.map((release) => (
            <div key={release.version} data-testid={`release-${release.version}`}>
              {/* Version Header */}
              <div className="flex items-center gap-3 mb-5">
                <h3 className="font-manrope font-bold text-xl text-text-primary">v{release.version}</h3>
                {release.label && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-primary/10 text-primary">
                    {release.label}
                  </span>
                )}
                <span className="text-sm text-text-muted ml-auto">{release.date}</span>
              </div>
              <p className="text-sm text-text-secondary mb-5 pl-1 border-l-2 border-primary/30 italic">
                {release.highlights}
              </p>

              {/* Sections */}
              <div className="space-y-6">
                {release.sections.map((section) => (
                  <div key={section.title}>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-text-muted mb-3">{section.title}</h4>
                    <div className="space-y-2">
                      {section.items.map((item) => (
                        <div key={item.text} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="bg-primary/8 p-1.5 rounded-md flex-shrink-0 mt-0.5">
                            <item.icon size={14} className="text-primary" />
                          </div>
                          <p className="text-sm text-text-secondary leading-relaxed">{item.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Divider between versions */}
              {release.version !== releases[releases.length - 1].version && (
                <div className="border-b border-gray-200 mt-8" />
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 text-center flex-shrink-0">
          <p className="text-xs text-text-muted">&copy; 2026 Sysqua Solutions Private Limited</p>
        </div>
      </div>
    </div>
  );
}
